import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

interface PopoverPlacementOptions {
  /** Fa sì che il menu abbia la stessa larghezza del controllo che lo apre. */
  matchAnchorWidth?: boolean
  /** Mantiene il pannello alla larghezza del proprio contenuto anche con controlli larghi. */
  fitContentWidth?: boolean
  /** Larghezza massima del pannello, in pixel. */
  maxWidth?: number
  align?: 'left' | 'right'
  onDismiss?: () => void
}

/** Posiziona i menu rispetto al viewport e li rende fuori dai contenitori che possono tagliarli. */
export function usePopoverPlacement({ matchAnchorWidth = false, fitContentWidth = false, maxWidth: panelMaxWidth, align = 'left', onDismiss }: PopoverPlacementOptions = {}) {
  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' })
  const anchorRef = useRef<HTMLDivElement>(null)
  const placementRef = useRef<HTMLElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return
      setOpen(false)
      onDismiss?.()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, onDismiss])

  useLayoutEffect(() => {
    if (!open) return
    const anchor = placementRef.current ?? anchorRef.current
    const pop = popRef.current
    if (!anchor || !pop) return

    const update = () => {
      const rect = anchor.getBoundingClientRect()
      const popupRect = pop.getBoundingClientRect()
      const gap = 4
      const margin = 8
      const viewportMaxWidth = Math.max(0, window.innerWidth - margin * 2)
      const maxWidth = Math.min(viewportMaxWidth, panelMaxWidth ?? viewportMaxWidth)
      const width = Math.min(maxWidth, fitContentWidth ? popupRect.width : matchAnchorWidth ? rect.width : Math.max(rect.width, popupRect.width))
      const height = Math.min(popupRect.height, window.innerHeight - margin * 2)
      const below = window.innerHeight - rect.bottom - gap
      const above = rect.top - gap
      const dropUp = below < height && above > below
      const desiredTop = dropUp ? rect.top - height - gap : rect.bottom + gap
      const top = Math.max(margin, Math.min(desiredTop, window.innerHeight - height - margin))
      const desiredLeft = align === 'right' ? rect.right - width : rect.left
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - width - margin))
      const maxHeight = window.innerHeight - margin * 2
      const next: CSSProperties = { position: 'fixed', top, left, width, maxWidth: panelMaxWidth ? `${maxWidth}px` : `calc(100vw - ${margin * 2}px)`, maxHeight, overflowY: 'auto', visibility: 'visible' }
      setPanelStyle(current => current.top === next.top && current.left === next.left && current.width === next.width && current.maxHeight === next.maxHeight && current.visibility === next.visibility ? current : next)
    }

    update()
    window.addEventListener('resize', update)
    document.addEventListener('scroll', update, true)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(anchor)
    observer?.observe(pop)
    return () => {
      window.removeEventListener('resize', update)
      document.removeEventListener('scroll', update, true)
      observer?.disconnect()
    }
  }, [open, matchAnchorWidth, fitContentWidth, panelMaxWidth, align])

  const anchor = anchorRef.current
  const nativeDialog = anchor?.closest<HTMLElement>('dialog')
  const customDialog = anchor?.closest<HTMLElement>('[role="dialog"]')
  const modalHost = customDialog?.parentElement?.querySelector<HTMLElement>(':scope > [data-popover-portal-host]')
  const nativeDialogHost = nativeDialog?.querySelector<HTMLElement>(':scope > [data-popover-portal-host]')
  const portalTarget = open
    ? anchor?.closest<HTMLElement>('[data-popover-portal-host]')
      ?? modalHost
      ?? nativeDialogHost
      ?? nativeDialog
      ?? (typeof document === 'undefined' ? null : document.body)
    : null

  return { open, setOpen, anchorRef, placementRef, popRef, panelStyle, portalTarget }
}

/** Compatibilità per i popover che non usano più il posizionamento assoluto. */
export function popoverPanelClass(_dropUp: boolean, _alignRight = false): string {
  return ''
}
